import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import { ITravelBlockPassengerParams } from "Bands/types";
import { merge, range } from "lodash-es";

interface IJSONAPITravelBlockPassenger {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPITravelBlockPassengerCollection {
  [IDataKeys.eventTravelBlockPassenger]: IJSONAPITravelBlockPassenger;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class TravelBlockPassengerService {
  static getPassengers = (
    queryParams?: {
      [key: string]: any;
    },
    isRecursive?: boolean
  ): Promise<IJSONAPITravelBlockPassengerCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        let passengers = await APIService.get({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK_PASSENGERS,
          data: queryParams,
        });

        if (!isRecursive) {
          resolve(passengers as IJSONAPITravelBlockPassengerCollection);
          return;
        }

        // get all passengers until last page
        const { currentPage, totalPages } = passengers.pagination || {};
        const toFetchPages = range(currentPage + 1, totalPages + 1);

        for (const page of toFetchPages) {
          const newPassengers = await APIService.get({
            path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK_PASSENGERS,
            data: {
              ...queryParams,
              page,
            },
          });

          passengers = merge({}, passengers, newPassengers);
        }

        passengers.pagination.currentPage = 1 as any;

        resolve(passengers as IJSONAPITravelBlockPassengerCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_TRAVEL_BLOCK_PASSENGERS);
        return reject(error);
      }
    });

  static createPassenger = (
    data: ITravelBlockPassengerParams
  ): Promise<IJSONAPITravelBlockPassengerCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK_PASSENGERS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_TRAVEL_BLOCK_PASSENGER);
        return reject(error);
      }
    });

  static editPassenger = ({
    id,
    data,
  }: {
    id: string;
    data: ITravelBlockPassengerParams;
  }): Promise<IJSONAPITravelBlockPassengerCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK_PASSENGER(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_TRAVEL_BLOCK_PASSENGER);
        return reject(error);
      }
    });

  static deletePassenger = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_TRAVEL_BLOCK_PASSENGER(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_TRAVEL_BLOCK_PASSENGER);
        return reject(error);
      }
    });
}
